import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import Uploader from "@/components/Uploader";
import { Plus, Edit2, Trash2, Eye, EyeOff, Save, X } from "lucide-react";

const CATEGORIES = [
  { key: "worker", label: "For Workers" },
  { key: "contractor", label: "For Contractors" },
  { key: "thought-leadership", label: "Industry / Thought Leadership" },
];

const EMPTY = {
  slug: "", title: "", excerpt: "", body: "", category: "worker",
  cover_image: "", tags: "", author: "Sarthi Associates Editorial", published: true,
};

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | postId
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/blog").then((r) => setPosts(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing("new"); setForm(EMPTY); };
  const openEdit = async (id) => {
    const p = posts.find((x) => x.id === id);
    if (!p) return;
    // Fetch full body
    const { data } = await api.get(`/blog/${p.slug}`).catch(() => ({ data: p }));
    setForm({ ...EMPTY, ...data, tags: (data.tags || []).join(", ") });
    setEditing(id);
  };
  const cancel = () => { setEditing(null); setForm(EMPTY); };
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target?.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async () => {
    if (!form.slug || !form.title || !form.body) return toast.error("Slug, title, and body are required");
    setBusy(true);
    try {
      const payload = { ...form, tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean) };
      if (editing === "new") {
        await api.post("/admin/blog", payload);
      } else {
        const { slug, ...updates } = payload;
        await api.patch(`/admin/blog/${editing}`, updates);
      }
      toast.success("Saved");
      setEditing(null); setForm(EMPTY);
      load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try { await api.delete(`/admin/blog/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const togglePublished = async (p) => {
    try { await api.patch(`/admin/blog/${p.id}`, { published: !p.published }); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  if (editing) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
        <h1 className="font-display text-3xl font-extrabold sm:text-4xl">{editing === "new" ? "New post" : "Edit post"}</h1>
        <div className="sarthi-card mt-6 space-y-4 p-6">
          <Field testid="post-slug" label="Slug (URL) — lowercase, hyphens only" value={form.slug} onChange={set("slug")} disabled={editing !== "new"} />
          <Field testid="post-title" label="Title" value={form.title} onChange={set("title")} />
          <Field testid="post-excerpt" label="Excerpt (max 500 chars)" value={form.excerpt} onChange={set("excerpt")} />
          <div>
            <label className="mb-1 block text-sm font-bold uppercase tracking-wide text-sarthi-muted">Cover image</label>
            <Uploader
              testid="post-cover-uploader"
              value={form.cover_image}
              onChange={(id) => setForm((s) => ({ ...s, cover_image: id ? `/api/files/${id}` : "" }))}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-bold uppercase tracking-wide text-sarthi-muted">Category</label>
            <select data-testid="post-category" value={form.category} onChange={set("category")} className="w-full rounded-xl border-2 border-sarthi-border bg-white px-4 py-3 text-base font-semibold focus:border-sarthi-primary focus:outline-none">
              {CATEGORIES.map((c) => (<option key={c.key} value={c.key}>{c.label}</option>))}
            </select>
          </div>
          <Field testid="post-category" label="" value={form.category} onChange={set("category")} hidden={true} />
          <Field testid="post-tags" label="Tags (comma-separated)" value={form.tags} onChange={set("tags")} />
          <Field testid="post-author" label="Author" value={form.author} onChange={set("author")} />
          <div>
            <label className="mb-1 block text-sm font-bold uppercase tracking-wide text-sarthi-muted">Body (Markdown supported)</label>
            <textarea data-testid="post-body" rows={16} value={form.body} onChange={set("body")} className="w-full rounded-xl border-2 border-sarthi-border bg-white px-4 py-3 font-mono text-sm focus:border-sarthi-primary focus:outline-none" />
          </div>
          <label className="inline-flex items-center gap-2 font-semibold">
            <input type="checkbox" data-testid="post-published" checked={form.published} onChange={set("published")} className="h-5 w-5" />
            Published
          </label>
          <div className="flex gap-3">
            <button data-testid="post-save" onClick={submit} disabled={busy} className="sarthi-btn-primary inline-flex items-center gap-2 px-5 py-3 text-white"><Save className="h-4 w-4"/> Save</button>
            <button data-testid="post-cancel" onClick={cancel} className="sarthi-btn-outline inline-flex items-center gap-2 px-5 py-3"><X className="h-4 w-4"/> Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl font-extrabold sm:text-4xl">Blog Posts</h1>
        <button data-testid="new-post-btn" onClick={openNew} className="sarthi-btn-primary inline-flex items-center gap-2 px-5 py-3 text-white"><Plus className="h-4 w-4"/> New post</button>
      </div>

      <div className="sarthi-card mt-6 overflow-x-auto">
        <table className="w-full text-left">
          <thead className="border-b-2 border-sarthi-border bg-sarthi-accent/50">
            <tr className="text-xs font-bold uppercase tracking-wide text-sarthi-muted">
              <th className="px-4 py-3">Title</th><th className="px-4 py-3">Category</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {posts.map((p) => (
              <tr key={p.id} data-testid={`post-row-${p.slug}`} className="border-b border-sarthi-border/60">
                <td className="px-4 py-3">
                  <div className="font-display text-base font-extrabold text-sarthi-text">{p.title}</div>
                  <div className="text-xs text-sarthi-muted">/{p.slug}</div>
                </td>
                <td className="px-4 py-3 text-sm font-semibold text-sarthi-muted">{p.category}</td>
                <td className="px-4 py-3">
                  <button onClick={() => togglePublished(p)} className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold ${p.published ? "bg-green-100 text-green-800" : "bg-stone-200 text-stone-700"}`}>
                    {p.published ? <Eye className="h-3 w-3"/> : <EyeOff className="h-3 w-3"/>}
                    {p.published ? "Published" : "Draft"}
                  </button>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button data-testid={`edit-${p.slug}`} onClick={() => openEdit(p.id)} className="rounded-lg border-2 border-sarthi-border bg-white p-2 hover:border-sarthi-primary"><Edit2 className="h-4 w-4"/></button>
                    <button data-testid={`delete-${p.slug}`} onClick={() => del(p.id)} className="rounded-lg border-2 border-red-200 bg-white p-2 text-red-700 hover:border-red-500"><Trash2 className="h-4 w-4"/></button>
                  </div>
                </td>
              </tr>
            ))}
            {posts.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-sarthi-muted">No posts</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, disabled }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-bold uppercase tracking-wide text-sarthi-muted">{label}</label>
      <input data-testid={testid} value={value} onChange={onChange} disabled={disabled} className="w-full rounded-xl border-2 border-sarthi-border bg-white px-4 py-3 text-base font-semibold focus:border-sarthi-primary focus:outline-none disabled:opacity-60" />
    </div>
  );
}
